import { useContext, useMemo } from 'react'
import { UserContext, UserContextType, MediaSources, DesktopCapturerSourceExt } from '../../UserContext'
import { getMidiDevices, getDesktopSources, getAudioAndVideoMediaSources } from '../../services/MediaDevices'
import { classifyAudioInputs } from '../../Logic'
import useAppContextRef from '../../services/useAppContextRef'
import useUserContextRef from '../../services/useUserContextRef'
import { logger } from '../../services/Logger'
import { max_channels_per_device } from '../../services/Constants'

const log = logger.createChildLogger({ name: 'MediaSourceActions' })

/**
 * Media Sources manager
 */
export default function useMediaSourceActions() {
    const { dispatch } = useContext(UserContext) as UserContextType

    const { appStateRef } = useAppContextRef()
    const { userStateRef } = useUserContextRef()

    return useMemo(() => {
        const setMediaSources = (mediaSources: Partial<MediaSources>) => {
            dispatch({
                type: 'update_media_sources',
                value: mediaSources,
            })
        }

        const loadAudioAndVideoMediaSources = async () => {
            try {
                const appSettings = appStateRef.current.appSettings

                log.verbose('loadAudioAndVideoMediaSources', `calling getAudioAndVideoMediaSources().`)

                dispatch({ type: 'update_media_source_status', value: { isLoading: true } })

                const mediaSources: Partial<MediaSources> = await getAudioAndVideoMediaSources({
                    origin: 'loadAudioAndVideoMediaSources',
                    forceShowAllAudioDevices: appSettings.forceShowAllAudioDevices,
                })
                mediaSources.loaded = true

                // for each audioInput media source, get from cache / create AudioInputSettings.

                setMediaSources(mediaSources)
                dispatch({ type: 'update_media_source_status', value: { isLoading: false } })

                log.verbose('loadAudioAndVideoMediaSources', `done.`)
                return mediaSources
            } catch (e) {
                log.error('loadAudioAndVideoMediaSources', `caught exception.`, { e })

                const mediaSources = { video: [], audioInputs: [], audioOutputs: [], audioOutputsAll: [] }

                setMediaSources(mediaSources)

                // TODO: prefereably set some error state.
                return mediaSources
            }
        }

        async function loadDesktopSources(): Promise<DesktopCapturerSourceExt[]> {
            try {
                dispatch({
                    type: 'update_media_source_status',
                    value: { desktop: { isLoading: true } },
                })

                const desktop = await getDesktopSources()
                log.checkpoint('load_desktop_sources', '', {
                    // only log the screens
                    desktop: desktop.filter((a) => (a.id || '').startsWith('screen:')),
                })

                setMediaSources({
                    desktop: desktop,
                })

                return desktop
            } catch (e) {
                log.error('loadDesktopSources', `caught exception`, { e })

                setMediaSources({
                    desktop: [],
                })

                return []
            } finally {
                dispatch({
                    type: 'update_media_source_status',
                    value: { desktop: { isLoading: false } },
                })
            }
        }

        const loadMidiSources = async () => {
            try {
                const { midiInputs, midiOutputs } = await getMidiDevices()
                setMediaSources({
                    midiInputs: midiInputs,
                    midiOutputs: midiOutputs,
                })
                return { midiInputs, midiOutputs }
            } catch (e) {
                log.error('loadMidiSources', `caught exception`, { e })
                return { midiInputs: [], midiOutputs: [] }
            }
        }

        const getAudioInputsClassifiedForUi = () => {
            const audioInputs = classifyAudioInputs(userStateRef.current.mediaSources.audioInputs)
            return audioInputs
        }

        // corresponds to the 'refresh devices list' button.
        const refreshMediaSourcesWithDebounce = () => {
            dispatch({
                type: 'update_media_sources',
                value: { _triggerUpdateWithDebounce: userStateRef.current.mediaSources._triggerUpdateWithDebounce + 1 },
            })
        }

        const generateChannelOptions = (
            currChannelSelection: number[][],
            maxInputChannels: number,
            options?: { skipSelected?: boolean },
            isVoiceQuality: boolean = false
        ) => {
            const usedMonoSlots = currChannelSelection.flat()

            const selectedChannelCt = currChannelSelection.reduce((a, b) => a + b.length, 0)
            console.log(
                `generateChannelOptions: currChannelSelection=${JSON.stringify(currChannelSelection)} ` +
                    `selectedChannelCt=${selectedChannelCt}`
            )
            if (selectedChannelCt >= max_channels_per_device) {
                return { channelOptions: [] as number[][], errorCode: 'max_channels_selected' }
            }

            //// mono
            let availableMonoSlots: number[][] = []
            let availableStereoSlots: number[][] = []
            for (let ch = 0; ch < maxInputChannels; ++ch) {
                if (options?.skipSelected) {
                    if (!usedMonoSlots.includes(ch)) {
                        availableMonoSlots.push([ch])
                    }
                } else {
                    availableMonoSlots.push([ch])
                }
            }

            //// stereo
            // note: give option to join, e.g. [1] -> [1,2]; we push into availableStereoSlots
            // unless an exact match already exists in currChannelSelection.
            for (let ch = 0; ch < maxInputChannels; ch += 2) {
                if (ch + 1 >= maxInputChannels) {
                    continue
                }

                let candidate = [ch, ch + 1]
                if (options?.skipSelected) {
                    if (!currChannelSelection.find((a) => JSON.stringify(a) === JSON.stringify(candidate))) {
                        availableStereoSlots.push(candidate)
                    }
                } else {
                    availableStereoSlots.push(candidate)
                }
            }

            const result = [...availableMonoSlots, ...availableStereoSlots]

            // sort list
            result.sort((a, b) => {
                if (a.length < b.length) {
                    return -1
                }
                if (a.length > b.length) {
                    return 1
                }
                return a[0] < b[0] ? -1 : 1
            })

            return { channelOptions: result, errorCode: null }
        }

        const getCurrentSystemDefaultOutputDevice = () => {
            const mediaSources = userStateRef.current.mediaSources.audioOutputs.filter((a) => !a.isSystemAudio)
            const gDevice = userStateRef.current.mediaSelection.globalOutputDevice || null
            if (!gDevice) {
                return null
            }

            const match = mediaSources.find((b) => {
                return gDevice.nativeDeviceId === b.nativeDeviceId || gDevice.uid === b.deviceUid
            })
            if (!match) {
                return null
            }

            if (match.isSystemAudio) {
                return null
            }

            return match
        }

        return {
            loadAudioAndVideoMediaSources,
            loadDesktopSources,
            loadMidiSources,
            refreshMediaSourcesWithDebounce,
            getAudioInputsClassifiedForUi,
            generateChannelOptions,
            getCurrentSystemDefaultOutputDevice,
        }
    }, [])
}

// useEffect(() => {
//     getMediaSourcesAndSet({ chooseDefaults: chooseDefaults })
// }, [])

// useEffect(() => {
//     /**
//      * On new device introduction:
//      *
//      * On device disconnected:
//      */
//     const deviceChangeHandler = async (event: Event) => {
//         // console.log('*** deviceChangeHandler=%O', event)

//         // if (isReady) {
//         //     const currMediaSelectionNext = clone(currMediaSelection)

//         //     if (!currMediaSelection.audioOutput || currMediaSelection.audioOutput === '_none' || currMediaSelection.audioOutput === '_undef') {
//         //     } else {
//         //         console.log(`at deviceChangeHandler: calling getMediaSourcesAndSet(). currMediaSelection=%O`, currMediaSelection)
//         //         const _mediaSources = await getMediaSourcesAndSet({ chooseDefaults: false })
//         //
//         //         // handle case where audio output device has become disconnected.
//         //         const has = (_mediaSources.audioOutputs || []).filter(a => a.deviceId === currMediaSelection.audioOutput).length > 0
//         //         if (!has) {
//         //             RootSnackbar.show({
//         //                 severity: 'warning',
//         //                 message: 'Speaker disconnected. Please select a new speaker.',
//         //             })

//         //             currMediaSelectionNext.audioOutput = '_none'
//         //         }
//         //     }

//         //     changeMediaSelection(currMediaSelectionNext)
//         // } else {
//         //     console.log(`at deviceChangeHandler: isReady==false so not doing anything.`)
//         // }
//     }

//     navigator.mediaDevices.addEventListener('devicechange', deviceChangeHandler)
//     return () => {
//         navigator.mediaDevices.removeEventListener('devicechange', deviceChangeHandler)
//     }
// }, [currMediaSelection, isReady])
